import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../lib/api";
import { LogOut, Inbox, Loader2, CreditCard, Wrench } from "lucide-react";
import { Logo } from "../components/Logo";
import { AdminLogin } from "../components/admin/AdminLogin";
import { StatsBar } from "../components/admin/StatsBar";
import { LeadsTab } from "../components/admin/LeadsTab";
import { PaymentsTab } from "../components/admin/PaymentsTab";
import { MaintenanceTab } from "../components/admin/MaintenanceTab";
import { ALERT_BOX } from "../components/admin/format";
import { useAdminData } from "../hooks/useAdminData";

const STRIPE_CLAIM_URL = "https://dashboard.stripe.com/onboard_sandbox/YWNjdF8xVUpTODlFUkZkRFE1UXJzLDE3OTA5MzY4MzAv1009ktXVGfQ";

const TabButton = ({ tab, active, onClick }) => (
  <button
    data-testid={`admin-tab-${tab.id}`}
    onClick={onClick}
    className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
      active ? "bg-navy text-white shadow-lg shadow-navy/25" : "border border-navy/15 text-slate-600 hover:border-gold hover:text-navy"
    }`}
  >
    <tab.icon size={15} />
    {tab.label}
    {tab.count > 0 && (
      <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] ${active ? "bg-gold text-navy" : "bg-gold/15 text-gold-dark"}`}>{tab.count}</span>
    )}
  </button>
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [tab, setTab] = useState("leads");
  const data = useAdminData(user);

  useEffect(() => {
    if (user) return;
    axios
      .get(`${API_BASE}/api/auth/me`, { withCredentials: true })
      .then((res) => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, [user]);

  const logout = async () => {
    try {
      await axios.post(`${API_BASE}/api/auth/logout`, {}, { withCredentials: true });
    } finally {
      setUser(null);
      setTab("leads");
      data.reset();
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <Loader2 className="animate-spin text-gold-dark" size={26} />
      </div>
    );
  }

  if (!user) return <AdminLogin onLogin={setUser} />;

  const TABS = [
    { id: "leads", label: "Demandes", icon: Inbox, count: data.leads.length },
    { id: "payments", label: "Paiements", icon: CreditCard, count: data.payments.length },
    { id: "maintenance", label: "Maintenance", icon: Wrench, count: data.requests.length },
  ];

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-page">
      <header className="border-b border-navy/10 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Logo />
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-semibold text-navy" data-testid="admin-user-name">{user.name}</p>
              <p className="text-xs text-slate-500" data-testid="admin-user-email">{user.email}</p>
            </div>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              aria-label="Se déconnecter"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-navy/15 text-navy transition-colors hover:border-gold hover:text-gold-dark"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        {data.paymentMode === "test" && (
          <a
            data-testid="admin-stripe-claim"
            href={STRIPE_CLAIM_URL}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-between gap-4 rounded-2xl border border-gold/40 bg-gold/10 px-5 py-4 text-sm text-navy transition-colors hover:bg-gold/20"
          >
            <span>
              <span className="font-semibold">Paiements en mode test.</span> Réclamez votre compte Stripe pour encaisser les 450 €, 700 € et 950 € sur votre propre compte.
            </span>
            <span className="shrink-0 font-semibold underline underline-offset-4">Réclamer maintenant</span>
          </a>
        )}
        {data.paymentMode === "live" && (
          <p className="flex items-center gap-2 text-xs font-medium text-slate-500" data-testid="admin-stripe-live">
            <span className="h-2 w-2 rounded-full bg-emerald-500" /> Paiements en ligne — Stripe live
          </p>
        )}

        <StatsBar payments={data.payments} subs={data.subs} leads={data.leads} requests={data.requests} />

        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="admin-tabs">
          {TABS.map((t) => <TabButton key={t.id} tab={t} active={tab === t.id} onClick={() => setTab(t.id)} />)}
        </div>

        {data.dataError && <p className={`mt-6 ${ALERT_BOX}`} data-testid="admin-data-error">{data.dataError}</p>}

        {tab === "leads" && <LeadsTab leads={data.leads} error={data.error} onFollowUp={data.followUpLead} onStatus={data.setLeadStatus} onAnalyze={data.analyzeLead} onSaveBrief={data.saveBrief} onDeliver={data.deliverSite} onSendPreview={data.sendPreview} />}
        {tab === "payments" && <PaymentsTab subs={data.subs} payments={data.payments} onCancelSub={data.cancelSub} />}
        {tab === "maintenance" && <MaintenanceTab requests={data.requests} onResolve={data.resolveRequest} />}
      </main>
    </div>
  );
}
